import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { combineLatest, Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { SsrmSelectAllPromptService } from './ssrm-select-all-prompt.service';

interface SelectAllBannerVm {
  visible: boolean;
  mode: 'page-prompt' | 'all-matching';
  selectedCount: number;
  itemTotal: number;
  inProgress: boolean;
}

/**
 * Two-state escalation banner for the SSRM grid (Gmail-style "select all
 * matching" UX).
 *
 * ## States (derived in `ngOnInit` from injected service + @Input observables)
 *
 * 1. **Hidden** (default): wrapper has `*ngIf="vm.visible === false"`.
 *    Activates when neither (a) the prompt is triggered nor (b) all matching
 *    are selected.
 * 2. **Page-prompt**: shows after the user clicks the SSRM grid's header
 *    checkbox to select the whole page AND there are more matching rows
 *    beyond the page. Wording: *"All {selectedCount} {itemLabel}s on this
 *    page are selected. Select all {itemTotal} {itemLabel}s [✕]"*. The
 *    "Select all" link shows a spinner while `selectAllInProgress$` is true.
 * 3. **All-matching**: shows when `selectedCount === itemTotal && itemTotal > 0`
 *    regardless of how the user got there. Wording: *"All {itemTotal}
 *    {itemLabel}s are selected. Clear selection"*. No close button — clear
 *    IS the primary action.
 *
 * ## Accessibility
 *
 * - `role="status"` + `aria-live="polite"` on the host so screen readers
 *   announce the appearance without interrupting the current utterance.
 * - The "Select all" link gets `aria-busy="true"` while
 *   `selectAllInProgress$` is true.
 * - Buttons (link + dismiss) are real `<button>` elements for keyboard
 *   accessibility.
 *
 * ## Why ngOnInit (not class field initializer) for viewModel$
 *
 * Class field initializers run during construction, BEFORE Angular sets
 * @Input bindings. If we built `viewModel$` from `combineLatest([this.itemTotal$, ...])`
 * at field-init time, we'd capture the default `of(0)` value forever and
 * never see the consumer's actual stream. ngOnInit runs after binding, so
 * the observables are correctly captured.
 */
@Component({
  selector: 'lib-ssrm-select-all-banner',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './ssrm-select-all-banner.component.html',
  styleUrls: ['./ssrm-select-all-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'status',
    'aria-live': 'polite',
    'aria-atomic': 'true',
  },
})
export class SsrmSelectAllBannerComponent implements OnInit {
  /** Total matching count from the backend (typically `itemTotal$` of a state service). */
  @Input() itemTotal$: Observable<number> = of(0);
  /** Current selection count (typically `selectedCount$` of a state service). */
  @Input() selectedCount$: Observable<number> = of(0);
  /** Pluralization root for messages, e.g. "device" → "devices". */
  @Input() itemLabel = 'item';

  @Output() selectAllMatchingClick = new EventEmitter<void>();
  /**
   * @deprecated Phase 7 of EDUS-2485 — the banner's "Clear selection"
   * link now drives the server-side `clearAllSelected` callback via the
   * grid's new `clearSelection()` method. Use `(clearAllSelectedClick)`
   * below instead. Kept on the public API so legacy callers compile;
   * the banner template emits BOTH this and the new event for now.
   */
  @Output() clearSelectionClick = new EventEmitter<void>();
  /**
   * Fired when the user clicks the banner's "Clear selection" link in
   * the all-matching mode. The grid's parent (CdwSsrmGridComponent's
   * template) wires this to the new `clearSelection()` method which
   * invokes the consumer's `[clearAllSelected]` callback.
   */
  @Output() clearAllSelectedClick = new EventEmitter<void>();
  @Output() dismissClick = new EventEmitter<void>();

  /**
   * Single combined view-model so the template binds via one async pipe and
   * OnPush only re-runs when an input observable emits.
   */
  protected viewModel$!: Observable<SelectAllBannerVm>;

  constructor(private readonly promptService: SsrmSelectAllPromptService) {}

  public ngOnInit(): void {
    this.viewModel$ = combineLatest([
      this.promptService.showPagePrompt$.pipe(startWith(false)),
      this.promptService.selectAllInProgress$.pipe(startWith(false)),
      // Defensive startWith on the consumer observables: if they haven't
      // emitted yet (e.g., before the first dataLoaded event), we render
      // hidden rather than wait for combineLatest to require a value from
      // each input.
      this.selectedCount$.pipe(startWith(0)),
      this.itemTotal$.pipe(startWith(0)),
    ]).pipe(
      map(
        ([
          showPagePrompt,
          inProgress,
          selectedCount,
          itemTotal,
        ]): SelectAllBannerVm => {
          const allMatchingSelected =
            itemTotal > 0 && selectedCount === itemTotal;
          const showPromptState =
            showPagePrompt && selectedCount > 0 && selectedCount < itemTotal;
          return {
            visible: allMatchingSelected || showPromptState,
            mode: allMatchingSelected ? 'all-matching' : 'page-prompt',
            selectedCount,
            itemTotal,
            inProgress,
          };
        }
      )
    );
  }

  protected onSelectAllMatchingClick(): void {
    this.selectAllMatchingClick.emit();
  }

  protected onClearSelectionClick(): void {
    // Emit BOTH events during the Phase 7→9 transition window so consumers
    // wired to either output keep working. Phase 9 deletes the deprecated
    // `clearSelectionClick` along with the consumer binding.
    this.clearSelectionClick.emit();
    this.clearAllSelectedClick.emit();
  }

  protected onDismissClick(): void {
    this.dismissClick.emit();
  }

  /** Suffix-pluralize for message rendering. Naive but adequate ("device" → "devices"). */
  protected pluralize(label: string): string {
    return `${label}s`;
  }
}
